/**
 * AdminContext — lightweight context that probes `/api/admin/me` on login
 * and exposes a boolean `isPlatformAdmin` for the sidebar to decide whether
 * to show the Admin entry.
 */
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AdminContext = createContext({ isPlatformAdmin: false, loading: true });
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminProvider({ children }) {
  const { token, user } = useAuth();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setIsPlatformAdmin(false);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => alive && setIsPlatformAdmin(true))
      .catch(() => alive && setIsPlatformAdmin(false))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, user?.id]);

  const value = useMemo(() => ({ isPlatformAdmin, loading }), [isPlatformAdmin, loading]);
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  return useContext(AdminContext);
}
